import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { TutorRole } from '../services/usersService';

export interface AddTutorInput {
  userId: string;
  displayName: string;
  email: string;
  role: TutorRole;
}

interface AddTutorDialogProps {
  open: boolean;
  saving?: boolean;
  /** Submit a new tutor. Caller handles refresh + snackbar. */
  onSubmit: (input: AddTutorInput) => void;
  onClose: () => void;
}

const ROLE_OPTIONS: { value: TutorRole; label: string; description: string }[] = [
  {
    value: 'viewer',
    label: 'Viewer',
    description: 'Nur Lesezugriff — sieht Runs / Logs aber loest keine Aktionen aus.',
  },
  {
    value: 'operator',
    label: 'Operator',
    description: 'Operative Aktionen (VMs starten / stoppen, Passwoerter, Teilnehmer).',
  },
  {
    value: 'admin',
    label: 'Admin',
    description: 'Vollzugriff inkl. Rollen-Verwaltung, Audit-Log, Azure Resources.',
  },
];

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function AddTutorDialog({ open, saving = false, onSubmit, onClose }: AddTutorDialogProps) {
  const [userId, setUserId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TutorRole>('viewer');

  useEffect(() => {
    if (open) {
      setUserId('');
      setDisplayName('');
      setEmail('');
      setRole('viewer');
    }
  }, [open]);

  const userIdTrim = userId.trim();
  const displayNameTrim = displayName.trim();
  const emailTrim = email.trim();
  const userIdValid = GUID_RE.test(userIdTrim);
  const emailValid = EMAIL_RE.test(emailTrim);
  const formValid = userIdValid && emailValid && displayNameTrim.length > 0;

  const userIdError = userIdTrim.length > 0 && !userIdValid;
  const emailError = emailTrim.length > 0 && !emailValid;

  const handleSubmit = () => {
    if (!formValid) return;
    onSubmit({
      userId: userIdTrim,
      displayName: displayNameTrim,
      email: emailTrim,
      role,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="add-tutor-dialog-title"
    >
      <DialogTitle id="add-tutor-dialog-title">Tutor anlegen</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Der neue Tutor muss bereits in Entra ID existieren. Kopieren Sie die Object-ID aus dem{' '}
          <Link
            href="https://portal.azure.com/#view/Microsoft_AAD_UsersAndTenants/UserManagementMenuBlade/~/AllUsers"
            target="_blank"
            rel="noopener"
          >
            Entra Admin Center
          </Link>{' '}
          (Benutzer → ausgewaehlter Benutzer → Objekt-ID).
        </DialogContentText>

        <Stack spacing={2}>
          <TextField
            label="AAD Object ID"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            disabled={saving}
            fullWidth
            required
            error={userIdError}
            helperText={
              userIdError
                ? 'Muss ein GUID sein, z.B. 11111111-1111-1111-1111-111111111111'
                : 'Aus Entra Admin Center → Benutzer → Objekt-ID'
            }
            inputProps={{ 'aria-label': 'AAD Object ID' }}
            autoFocus
          />
          <TextField
            label="Anzeigename"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={saving}
            fullWidth
            required
            inputProps={{ 'aria-label': 'Anzeigename' }}
          />
          <TextField
            label="E-Mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={saving}
            fullWidth
            required
            error={emailError}
            helperText={emailError ? 'Bitte eine gueltige E-Mail-Adresse eingeben.' : ' '}
            inputProps={{ 'aria-label': 'E-Mail' }}
          />
          <FormControl fullWidth disabled={saving}>
            <InputLabel id="add-tutor-role-label">Rolle</InputLabel>
            <Select
              labelId="add-tutor-role-label"
              label="Rolle"
              value={role}
              onChange={(e) => setRole(e.target.value as TutorRole)}
              inputProps={{ 'aria-label': 'Rolle' }}
            >
              {ROLE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            {ROLE_OPTIONS.find((opt) => opt.value === role)?.description}
          </Typography>

          {role === 'admin' && (
            <Alert severity="warning">
              Admin-Rolle gibt Vollzugriff inkl. Rollen-Verwaltung — nur fuer vertrauenswuerdige
              Tutoren vergeben.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={role === 'admin' ? 'warning' : 'primary'}
          disabled={saving || !formValid}
          startIcon={saving ? <CircularProgress size={18} /> : undefined}
        >
          {saving ? 'Anlegen…' : 'Anlegen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddTutorDialog;
